import { chakra, Text, TextProps } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';

import {
  CmBox,
  CmButton,
  CmCard,
  CmFlex,
  CmHelpPopover,
  CmLongLoading,
  CmMessageCard,
  CmStack,
  CmText,
} from '@/components/uiParts';
import { searchProviderFromName } from '@/domain/models/Enterprise';
import {
  PosDataLevel,
  PosGetItem,
  PosOption,
  PosProvider,
  posProviderNameToJa,
} from '@/domain/models/Pos';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { PosDataFactory } from '@/pages/externals/pos-connect/factories';
import { useEnterprise } from '@/services/Enterprise/hooks';
import {
  mappingPosDataLevel,
  PosItemAndHandlePos,
  posItemColumn,
} from '@/services/Pos';
import { usePos } from '@/services/Pos/hooks';

import { CSVImportReader } from './CSVImportReader';
import { PosCodeUpdateModal } from './PosCodeUpdateModal';
import { PosItemTable } from './PosItemTable';

// @ts-ignore TS2590
export const CardTitle: React.FC<TextProps> = chakra(Text, {
  baseStyle: {
    fontSize: 'lg',
    fontWeight: 'bold',
  },
});

interface Props {
  selectedPosProvider: string;
}

export const PosItemsArea: React.VFC<Props> = ({ selectedPosProvider }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enterprise } = useEnterprise();
  const [posProviderId, setPosProviderId] = useState<number>();
  const [posDataLevel, setPosDataLevel] = useState<PosDataLevel>();
  const [posOptions, setPosOptions] = useState<PosOption[]>([]);

  const [posItems, setPosItems] = useState<PosItemAndHandlePos[]>([]);

  const posItemsAtStorage =
    LocalStorageHandler.get('draft_pos_data')[selectedPosProvider];

  const posOption = posOptions.find((option) => option.value);

  const confirmPosItems = (() => {
    if (posItemsAtStorage === undefined) return [];

    return posItems.filter((posItem, index) => {
      // if (posItemsAtStorage[index]) の分岐処理がないとローカルのモックデータが各DataLevelの違うデーターがきてエラーが起きるため入れている。
      if (posItemsAtStorage[index]) {
        // NOTE posItem.posCodeを登録しないとpromotionOneFreePosCodeは登録が出来ないという仕様
        if (posItem.posCode === '' && posItem.promotionOneFreePosCode !== '') {
          return false;
        }

        return (
          // NOTE DBの値と同じではなく、入力をされている状態
          posItem.posCode !== posItem.pos_item_code ||
          posItem.promotionOneFreePosCode !==
            posItem.buy_one_get_one_free_pos_item_code
        );
      }
    });
  })();

  const isExistBlankOnlyPosCode = posItems.some((posItem) => {
    return posItem.posCode === '' && posItem.promotionOneFreePosCode !== '';
  });

  const isExistPosItems = posItems.length > 0;
  const isDisabledUpdateButton =
    confirmPosItems.length === 0 || !isExistPosItems || isExistBlankOnlyPosCode;

  const setStorage = (posItems: PosItemAndHandlePos[]) => {
    LocalStorageHandler.set('draft_pos_data', {
      [selectedPosProvider]: posItems,
    });
  };

  /* csv処理郡 */
  const operateCsv = {
    isPromotionOneFree: (options: PosOption[]) => {
      return options.some(
        (o) => o.type === 'promotion' && o.value === 'buy_one_get_one_free'
      );
    },

    posItemCSVData: () => {
      if (posItemsAtStorage === undefined) return [];
      if (posDataLevel === undefined) return [];

      return PosDataFactory.createCSVFromItems(posItemsAtStorage, posOptions);
    },

    posItemCSVHeader: () => {
      if (posDataLevel === undefined) return [];
      return PosDataFactory.createCSVHeaderFromItems(posOptions, posDataLevel);
    },
  };

  const { posItemCSVData, posItemCSVHeader } = operateCsv;

  /* PosCodeのハンドラー */
  const operatePosCode = {
    handleInputPosCode: (index: number, newPosCode: string) => {
      const copyPosItems = [...posItems];
      copyPosItems[index].posCode = newPosCode;

      setPosItems(copyPosItems);
      setStorage(copyPosItems);
    },

    handleResetPosCode: (index: number) => {
      const copyPosItems = [...posItems];
      copyPosItems[index].posCode = copyPosItems[index].pos_item_code ?? '';

      setPosItems(copyPosItems);
      setStorage(copyPosItems);
    },
  };

  const { handleInputPosCode, handleResetPosCode } = operatePosCode;

  /* PromotionOneFreePosCodeのハンドラー */
  const operatePromotionOneFreePosCode = {
    handleInputPromotionOneFreePosCode: (
      index: number,
      newPromotionOneFreePosCode: string
    ) => {
      const copyPosItems = [...posItems];
      copyPosItems[index].promotionOneFreePosCode = newPromotionOneFreePosCode;

      setPosItems(copyPosItems);
      setStorage(copyPosItems);
    },

    handleResetPromotionOneFreePosCode: (index: number) => {
      const copyPosItems = [...posItems];
      copyPosItems[index].promotionOneFreePosCode =
        copyPosItems[index].buy_one_get_one_free_pos_item_code ?? '';
      setPosItems(copyPosItems);
      setStorage(copyPosItems);
    },
  };

  const {
    handleInputPromotionOneFreePosCode,
    handleResetPromotionOneFreePosCode,
  } = operatePromotionOneFreePosCode;

  /* 初期化処理 */
  const initialize = {
    commonSetter: (
      posGetItem: PosGetItem,
      customPosItems: PosItemAndHandlePos[]
    ) => {
      setPosOptions(posGetItem.options || []);
      setPosItems(customPosItems);
      setStorage(customPosItems);
      setIsLoading(false);
    },

    reFetchPosItems: async () => {
      if (posProviderId === undefined || posDataLevel === undefined) return;
      setIsLoading(true);
      const posGetItem = await usePos({
        providerId: posProviderId,
        dataLevel: posDataLevel,
      });
      setIsLoading(false);

      if (!posGetItem.pos_items) {
        setPosItems([]);
        return;
      }

      const customPosItems = PosDataFactory.createHandlePosItemsAtReFeatch(
        posGetItem.pos_items
      );
      initialize.commonSetter(posGetItem, customPosItems);
    },

    fetchPosItems: async (
      posProviderId: PosProvider['pos_provider_id'],
      posProviderLevel: PosProvider['pos_data_level']
    ) => {
      setIsLoading(true);
      const posGetItem = await usePos({
        providerId: posProviderId,
        dataLevel: posProviderLevel,
      });
      setIsLoading(false);

      if (!posGetItem.pos_items) {
        setPosItems([]);
        return;
      }

      const customPosItems = PosDataFactory.createHandlePosItems(
        posGetItem.pos_items,
        posItemsAtStorage
      );

      initialize.commonSetter(posGetItem, customPosItems);
    },
  };

  const { reFetchPosItems } = initialize;

  const handleResetPosItems = () => {
    LocalStorageHandler.set('draft_pos_data', {
      [selectedPosProvider]: [],
    });

    reFetchPosItems();
  };

  useEffect(() => {
    (async () => {
      /* ここからprovider情報を取得 */
      if (enterprise === undefined) return;
      const posProvider = searchProviderFromName(
        enterprise,
        selectedPosProvider
      );

      if (posProvider === undefined) return;
      setPosProviderId(posProvider.provider_id);
      setPosDataLevel(posProvider.pos_data_level);

      /* ここからposGetItemの情報をセット */
      initialize.fetchPosItems(
        posProvider.provider_id,
        posProvider.pos_data_level
      );
    })();
  }, [selectedPosProvider, enterprise]);

  if (posDataLevel === undefined) return <></>;

  return (
    <>
      {isLoading && <CmLongLoading />}
      {posProviderId &&
        (posItems.length > 0 ? (
          <CmStack>
            <CmCard mt={2}>
              <CmFlex justifyContent="space-between" alignItems="center">
                <CardTitle fontSize="25px" mb={2}>
                  CSV連携
                </CardTitle>
                <CmHelpPopover wrapSize="size-600">
                  <CmText>
                    CSVファイル をダウンロードして、CSVファイル に POSコード
                    を入力してください。
                  </CmText>
                  <CmText mt="5px">
                    入力完了後、POSコード を入力した CSVファイル
                    をアップロードし、ファイルが正しいことを確認して登録ボタンを押してください。
                  </CmText>
                  <CmText mt="5px" fontWeight="bold" color="primaryRed">
                    {posItemColumn.metaData.label} は書き換えないでください。
                  </CmText>
                </CmHelpPopover>
              </CmFlex>
              <CmFlex justify="space-between" mt={4} alignItems="end">
                <CSVLink
                  data={posItemCSVData()}
                  headers={posItemCSVHeader()}
                  filename={PosDataFactory.createFileName()}
                >
                  <CmButton>CSVダウンロード</CmButton>
                </CSVLink>

                <CSVImportReader {...{ reFetchPosItems, posProviderId }} />
              </CmFlex>
            </CmCard>

            <CmCard>
              <CmFlex justifyContent="space-between" alignItems="center">
                <CardTitle fontSize="25px" mb={2}>
                  POSコード登録
                </CardTitle>
                <CmHelpPopover wrapSize="size-600">
                  <CmText>
                    複数のブラウザのタブで操作をすると、他のタブで更新を行ったとしても、現在のタブで入力中のデータがあった場合にはデータの更新が行われないため、最新のデータが表示をされません。
                    <br />
                    最新の保存データを取得する際は、『リセット』ボタンを押してください。
                  </CmText>
                  <CmText mt="5px" fontWeight="bold" color="primaryRed">
                    リセット時は入力途中のデータが消えることにご注意ください。
                  </CmText>
                </CmHelpPopover>
              </CmFlex>

              <CmFlex
                mt={4}
                justifyContent="space-between"
                pb="10px"
                borderBottom="1px solid #EDF2F7"
              >
                {/* NOTE 他のページでも似たようなものがあるので、こちらのラベルとバリュー値の汎用コンポーネントを作成しても良いかも。*/}
                <div>
                  <CmFlex>
                    <CmText width="150px" fontWeight="bold">
                      POS事業者
                    </CmText>
                    <CmText>{posProviderNameToJa(selectedPosProvider)}</CmText>
                  </CmFlex>
                  <CmFlex>
                    <CmText width="150px" fontWeight="bold">
                      POSデータレベル
                    </CmText>
                    <CmText>{mappingPosDataLevel[posDataLevel].toJa}</CmText>
                  </CmFlex>
                </div>

                <CmBox>
                  <CmFlex>
                    <CmButton
                      mr="30px"
                      onClick={handleResetPosItems}
                      variant="outline"
                    >
                      リセット
                    </CmButton>

                    <PosCodeUpdateModal
                      {...{
                        posDataLevel,
                        posProviderId,
                        posOption,
                        confirmPosItems,
                        isDisabledUpdateButton,
                        reFetchPosItems,
                      }}
                    />
                  </CmFlex>
                </CmBox>
              </CmFlex>

              <PosItemTable
                {...{
                  posItems,
                  posOption,
                  posDataLevel,
                  handleInputPosCode,
                  handleInputPromotionOneFreePosCode,
                  handleResetPromotionOneFreePosCode,
                  handleResetPosCode,
                }}
              />
            </CmCard>
          </CmStack>
        ) : (
          !isLoading && (
            <CmMessageCard margin="100px auto 0">
              登録できる商品がありません。
            </CmMessageCard>
          )
        ))}
    </>
  );
};
